import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Settings, CreditCard } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';
import { useTheme } from '../contexts/ThemeContext';
import { APP_NAME } from '../config/branding';

const DocsPage: React.FC = () => {
  const [activeTab, setActiveTab] = React.useState('getting-started');
  const { effectiveTheme } = useTheme();
  const isDark = effectiveTheme === 'dark';
  
  usePageTitle('Documentation & Setup Guide - Get Started with AI Veterinary Phone System');

  // Theme-aware colors
  const colors = {
    bg: isDark ? '#0f1419' : '#fff',
    bgSecondary: isDark ? '#1a1f26' : '#f6f9fc',
    bgTertiary: isDark ? '#242b33' : '#f6f9fc',
    text: isDark ? '#e7e9ea' : '#1a1a1a',
    textSecondary: isDark ? '#8b98a5' : '#525f7f',
    textMuted: isDark ? '#6e767d' : '#8898aa',
    border: isDark ? '#2f3336' : '#e6e6e6',
    borderLight: isDark ? '#404549' : '#c7d2fe',
    link: isDark ? '#93c5fd' : '#2563eb',
  };

  return (
    <>
      <style>
        {`
          .docs-page ::selection {
            background-color: ${isDark ? 'rgba(59, 130, 246, 0.4)' : '#b3d4fc'};
            color: ${isDark ? '#ffffff' : '#000000'};
          }
          .docs-page ::-moz-selection {
            background-color: ${isDark ? 'rgba(59, 130, 246, 0.4)' : '#b3d4fc'};
            color: ${isDark ? '#ffffff' : '#000000'};
          }
          .feedback-button {
            background-color: ${isDark ? '#242b33' : '#f6f9fc'};
            border: 1px solid ${isDark ? '#2f3336' : '#c7d2fe'};
            border-radius: 6px;
            padding: 8px 16px;
            fontSize: 14px;
            color: ${isDark ? '#e7e9ea' : '#525f7f'};
            cursor: pointer;
            transition: all 0.2s ease;
            font-weight: 500;
          }
          .feedback-button:hover {
            background-color: ${isDark ? '#2d353f' : '#e0f2fe'};
            border-color: ${isDark ? '#404549' : '#93c5fd'};
            color: ${isDark ? '#ffffff' : '#1e40af'};
            transform: translateY(-1px);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }
          .feedback-button:active {
            transform: translateY(0) scale(0.98);
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
          }
        `}
      </style>
      <div className="docs-page" style={{
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        color: colors.text,
        backgroundColor: colors.bg,
        fontSize: '16px',
        lineHeight: 1.6,
        minHeight: '100vh'
      }}>
      {/* Header */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: colors.bg,
        borderBottom: `1px solid ${colors.border}`
      }}>
        <div style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none' }}>
              <img 
                src="/logo_clear_back.png" 
                alt={`${APP_NAME} Logo`}
                width="32" 
                height="32" 
                className="object-contain"
              />
              <span style={{
                color: colors.text,
                fontSize: '20px',
                fontWeight: 600,
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, "Apple Color Emoji", Arial, sans-serif',
                letterSpacing: '-0.01em'
              }}>
                {APP_NAME} Docs
              </span>
            </Link>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Link
              to="/signup"
              style={{
                backgroundColor: isDark ? '#2563eb' : '#1a1a1a',
                color: '#fff',
                padding: '8px 16px',
                borderRadius: '6px',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: 500
              }}
            >
              Sign up free
            </Link>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex' }}>
        {/* Sidebar */}
        <nav style={{
          width: '240px',
          position: 'sticky',
          top: '65px',
          height: 'calc(100vh - 65px)',
          overflowY: 'auto',
          borderRight: `1px solid ${colors.border}`,
          padding: '32px 0',
          backgroundColor: colors.bg
        }}>
          <div style={{ padding: '0 24px' }}>
            <div style={{ marginBottom: '24px' }}>
              <h6 style={{
                fontSize: '12px',
                fontWeight: 600,
                color: colors.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                margin: '0 0 12px 0'
              }}>
                On this page
              </h6>
              
              <p style={{
                fontSize: '14px',
                color: colors.textSecondary,
                lineHeight: 1.5
              }}>
                Set up your AI voice agent in minutes. No technical knowledge required.
              </p>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h6 style={{
                fontSize: '12px',
                fontWeight: 600,
                color: colors.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                margin: '0 0 12px 0'
              }}>
                Quick Links
              </h6>
              <Link 
                to="/dashboard/voice-agents"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 0',
                  textDecoration: 'none'
                }}
              >
                <Phone size={14} color={colors.textSecondary} />
                <span style={{ fontSize: '14px', color: colors.textSecondary, fontWeight: 500 }}>Voice Agents</span>
              </Link>
              <Link 
                to="/dashboard/phones"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 0',
                  textDecoration: 'none'
                }}
              >
                <Settings size={14} color={colors.textSecondary} />
                <span style={{ fontSize: '14px', color: colors.textSecondary, fontWeight: 500 }}>Phone Setup</span>
              </Link>
              <a 
                href="mailto:hi@helppet.ai"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 0',
                  textDecoration: 'none'
                }}
              >
                <CreditCard size={14} color={colors.textSecondary} />
                <span style={{ fontSize: '14px', color: colors.textSecondary, fontWeight: 500 }}>Contact Sales</span>
              </a>
            </div>
          </div>
        </nav>

        <main style={{
          flex: 1,
          padding: '48px 48px 96px 48px',
          backgroundColor: colors.bg
        }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: 600,
            color: colors.text,
            margin: '0 0 16px 0',
            lineHeight: 1.2
          }}>
            Voice agent setup
          </h1>
          <p style={{
            fontSize: '20px',
            color: colors.textSecondary,
            margin: '0 0 32px 0',
            lineHeight: 1.5
          }}>
            Quickstart - Integrate with {APP_NAME} in minutes
          </p>

          <div style={{
            backgroundColor: colors.bgSecondary,
            border: `1px solid ${colors.borderLight}`,
            borderRadius: '8px',
            padding: '24px',
            marginBottom: '32px'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 600,
              color: colors.text,
              margin: '0 0 16px 0'
            }}>
              Before you start
            </h3>
            <div style={{ fontSize: '15px', color: colors.textSecondary, lineHeight: 1.7 }}>
              <p style={{ margin: '0 0 12px 0' }}>
                Are you a veterinary professional? <a href="/signup" style={{ color: colors.link, textDecoration: 'none', fontWeight: 500 }}>Sign up at helppet.ai</a> to get started.
              </p>
              <p style={{ margin: 0 }}>
                Experience {APP_NAME}'s capabilities by calling our demo AI agent at <strong style={{ color: colors.text }}></strong>.
              </p>
            </div>
          </div>

          <div style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '32px',
            borderBottom: `1px solid ${colors.border}`
          }}>
            <button 
              onClick={() => setActiveTab('getting-started')}
              style={{
                padding: '12px 16px',
                border: 'none',
                borderBottom: activeTab === 'getting-started' ? '2px solid #2563eb' : '2px solid transparent',
                backgroundColor: 'transparent',
                color: activeTab === 'getting-started' ? colors.link : colors.textMuted,
                fontSize: '14px',
                fontWeight: activeTab === 'getting-started' ? 600 : 400,
                cursor: 'pointer'
              }}
            >
              Getting Started
            </button>
            <button 
              onClick={() => setActiveTab('configuration')}
              style={{
                padding: '12px 16px',
                border: 'none',
                borderBottom: activeTab === 'configuration' ? '2px solid #2563eb' : '2px solid transparent',
                backgroundColor: 'transparent',
                color: activeTab === 'configuration' ? colors.link : colors.textMuted,
                fontSize: '14px',
                fontWeight: activeTab === 'configuration' ? 600 : 400,
                cursor: 'pointer'
              }}
            >
              Configuration
            </button>
            <button 
              onClick={() => setActiveTab('billing')}
              style={{
                padding: '12px 16px',
                border: 'none',
                borderBottom: activeTab === 'billing' ? '2px solid #2563eb' : '2px solid transparent',
                backgroundColor: 'transparent',
                color: activeTab === 'billing' ? colors.link : colors.textMuted,
                fontSize: '14px',
                fontWeight: activeTab === 'billing' ? 600 : 400,
                cursor: 'pointer'
              }}
            >
              Billing
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'getting-started' && (
            <div>
              <h2 style={{ fontSize: '28px', fontWeight: 600, marginBottom: '16px', color: colors.text }}>Step-by-step Setup</h2>
              
              <div style={{
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                overflow: 'hidden',
                marginBottom: '32px',
                backgroundColor: colors.bg
              }}>
                {[
                  {
                    step: '1',
                    title: 'Create Voice Agent',
                    description: 'Navigate to Voice Agents in your dashboard and click "Set up voice agent". The agent is created instantly with AI-powered conversation flows.'
                  },
                  {
                    step: '2',
                    title: 'Contact Sales',
                    description: 'Reach out to our sales team at hi@helppet.ai to get your account activated and phone line set up.'
                  },
                  {
                    step: '3',
                    title: 'Configure Phone Number',
                    description: 'Choose between a regular phone number ($10/mo) or toll-free number ($15/mo). Select your area code and activate.'
                  },
                  {
                    step: '4',
                    title: 'Test Your Agent',
                    description: 'Call your new number to test the agent. It will answer automatically and guide callers through appointment booking, practice information, and more.'
                  }
                ].map((item, index) => (
                  <div key={index} style={{
                    padding: '24px',
                    borderBottom: index < 3 ? `1px solid ${colors.border}` : 'none',
                    display: 'flex',
                    gap: '20px'
                  }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: isDark ? 'rgba(37, 99, 235, 0.2)' : '#dbeafe',
                      color: colors.link,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      fontSize: '16px',
                      flexShrink: 0
                    }}>
                      {item.step}
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: 600,
                        color: colors.text,
                        margin: '0 0 8px 0'
                      }}>
                        {item.title}
                      </h3>
                      <p style={{
                        fontSize: '14px',
                        color: colors.textSecondary,
                        margin: 0,
                        lineHeight: 1.5
                      }}>
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'configuration' && (
            <div>
              <h2 style={{ fontSize: '28px', fontWeight: 600, marginBottom: '16px', color: colors.text }}>Agent Configuration</h2>
              
              <div style={{
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                overflow: 'hidden',
                marginBottom: '32px',
                backgroundColor: colors.bg
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 2fr',
                  backgroundColor: colors.bgTertiary,
                  padding: '16px 20px',
                  borderBottom: `1px solid ${colors.border}`
                }}>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Setting
                  </div>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Description
                  </div>
                </div>

                {[
                  {
                    name: 'Timezone',
                    description: 'Set your practice timezone for accurate appointment scheduling. The agent will use this for availability checks and booking confirmations.'
                  },
                  {
                    name: 'Welcome Message',
                    description: 'The first thing callers hear. Keep it friendly and professional. Include your practice name and a warm greeting.'
                  },
                  {
                    name: 'Phone Type',
                    description: 'Regular phone ($10/mo) or Toll-Free ($15/mo). Toll-free numbers are more professional but cost extra.'
                  },
                  {
                    name: 'Area Code',
                    description: 'Choose an area code for your phone number. Local area codes help build trust with nearby clients.'
                  }
                ].map((setting, index) => (
                  <div key={index} style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 2fr',
                    padding: '20px',
                    borderBottom: index < 3 ? `1px solid ${colors.border}` : 'none'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: colors.text,
                      paddingRight: '20px'
                    }}>
                      {setting.name}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: colors.textSecondary,
                      lineHeight: 1.5
                    }}>
                      {setting.description}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'billing' && (
            <div>
              <h2 style={{ fontSize: '28px', fontWeight: 600, marginBottom: '16px', color: colors.text }}>Pricing & Usage</h2>
              
              <div style={{
                backgroundColor: isDark ? 'rgba(37, 99, 235, 0.15)' : '#dbeafe',
                border: `1px solid ${isDark ? 'rgba(59, 130, 246, 0.4)' : '#93c5fd'}`,
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '24px'
              }}>
                <p style={{
                  margin: 0,
                  fontSize: '15px',
                  color: isDark ? '#93c5fd' : '#1e40af',
                  lineHeight: 1.6
                }}>
                  <strong style={{ fontSize: '16px' }}>Custom Pricing</strong><br />
                  All pricing is negotiated with our sales team. Contact <a href="mailto:hi@helppet.ai" style={{ color: 'inherit', textDecoration: 'underline' }}>hi@helppet.ai</a> for details.
                </p>
              </div>

              <div style={{
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                overflow: 'hidden',
                marginBottom: '32px',
                backgroundColor: colors.bg
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 2fr',
                  backgroundColor: colors.bgTertiary,
                  padding: '16px 20px',
                  borderBottom: `1px solid ${colors.border}`
                }}>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Feature
                  </div>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Price
                  </div>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Details
                  </div>
                </div>

                {[
                  {
                    feature: 'Regular Phone Number',
                    price: '$10/month',
                    details: 'Monthly subscription for a dedicated phone line with your chosen area code.'
                  },
                  {
                    feature: 'Toll-Free Number',
                    price: '$15/month',
                    details: 'Premium toll-free phone number (1-800, 1-888, etc.) for a more professional presence.'
                  },
                  {
                    feature: 'Voice AI Calls',
                    price: 'See pricing',
                    details: 'See plan details and overages on the Pricing page.'
                  }
                ].map((item, index) => (
                  <div key={index} style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 2fr',
                    padding: '20px',
                    borderBottom: index < 2 ? `1px solid ${colors.border}` : 'none'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: colors.text,
                      paddingRight: '20px'
                    }}>
                      {item.feature}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: colors.link,
                      paddingRight: '20px'
                    }}>
                      {item.price}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: colors.textSecondary,
                      lineHeight: 1.5
                    }}>
                      {item.details}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{
                backgroundColor: isDark ? 'rgba(250, 204, 21, 0.15)' : '#fef3c7',
                border: `1px solid ${isDark ? 'rgba(250, 204, 21, 0.4)' : '#fbbf24'}`,
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '32px'
              }}>
                <p style={{
                  margin: 0,
                  fontSize: '14px',
                  color: isDark ? '#fcd34d' : '#92400e'
                }}>
                  <strong>Important</strong><br />
                  Credits are applied to your first purchase only and are non-refundable. If you remove a phone number, credits are not returned.
                </p>
              </div>
            </div>
          )}

          {/* Feedback section */}
          <div style={{
            borderTop: `1px solid ${colors.border}`,
            paddingTop: '32px',
            marginTop: '64px'
          }}>
            <p style={{
              fontSize: '16px',
              fontWeight: 600,
              color: colors.text,
              marginBottom: '16px'
            }}>
              Was this page helpful?
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button className="feedback-button">
                Yes
              </button>
              <button className="feedback-button">
                No
              </button>
            </div>
          </div>
        </main>
      </div>
      </div>
    </>
  );
};

export default DocsPage;